import React from 'react';
import Barcode from 'react-barcode';

type BarcodeFormat = 
  "CODE128" | "CODE39" | "CODE128A" | "CODE128B" | "CODE128C" | 
  "EAN13" | "EAN8" | "EAN5" | "EAN2" | "UPC" | "UPCE" | 
  "ITF14" | "ITF" | "MSI" | "MSI10" | "MSI11" | "MSI1010" | 
  "MSI1110" | "pharmacode" | "codabar" | "GenericBarcode";

interface BarcodeOptions {
  width?: number;
  height?: number;
  format?: BarcodeFormat;
  displayValue?: boolean;
  fontOptions?: string;
  font?: string;
  textAlign?: string;
  textPosition?: string;
  textMargin?: number;
  fontSize?: number;
  background?: string;
  lineColor?: string;
  margin?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
}

interface BarcodeData {
  sku: string;
  code?: string;
  sizeInfo?: string;
  quantity?: number;
  extraInfo?: string;
  productName?: string;
}

// Function to print barcodes for product variants
export const printBarcodes = (productVariants: any[], productName: string): void => {
  if (!productVariants || productVariants.length === 0) {
    console.error("No product variants provided");
    return;
  }

  // Create barcode data from product variants
  const barcodeData: BarcodeData[] = productVariants.map(variant => ({
    sku: variant.sku,
    code: variant.sku, // Using SKU as the barcode code
    sizeInfo: `${variant.productsizesProductvariants?.name || ''}`,
    quantity: variant.packOf || 0,
    extraInfo: `${new Date().getFullYear().toString().slice(-2)}${parseInt(variant.unitPrice) || 0}${new Date().getFullYear().toString().slice(-2)}`,
    productName: `${variant.productName ? variant.productName : ''}`,
  }));

  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Failed to open print window. Pop-ups might be blocked.');
    return;
  }

  const createBarcodeElements = () => {
    const renderRoot = printWindow.document.getElementById('barcode-root');
    if (!renderRoot) return;

    const react = require('react');
    const ReactDOM = require('react-dom');

    ReactDOM.render(
      <BarcodeContainer barcodeData={barcodeData} />,
      renderRoot
    );
  };

  // Create HTML content for the print window
  const htmlContent = `
    <html>
      <head>
        <title>Print Barcodes</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 10px;
          }
          .barcode-container {
            padding: 10px;
            border: 1px solid #ccc;
            margin-bottom: 10px;
            page-break-inside: avoid;
            width: 300px;
            display: inline-block;
            margin-right: 10px;
            text-align: center;
          }
          .barcode-header {
            text-align: center;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .barcode-size {
            font-weight: bold;
            margin-top: 5px;
            font-size: 11px;
          }
          .barcode-qty {
            margin-top: 2px;
            font-size: 11px;
            font-weight: bold;
          }
          .barcode-info {
            font-size: 11px;
            margin-top: 2px;
          }
          .barcode-product-name {
            font-size: 11px;
            margin-top: 2px;
          }
          @media print {
            .no-print {
              display: none;
            }
          }
        </style>
        <script src="https://unpkg.com/react@17/umd/react.production.min.js"></script>
        <script src="https://unpkg.com/react-dom@17/umd/react-dom.production.min.js"></script>
        <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
      </head>
      <body>
        <div class="no-print" style="margin-bottom: 20px;">
          <button onclick="window.print()">Print</button>
          <button onclick="window.close()">Close</button>
        </div>
        <div id="barcode-print-area">
          ${barcodeData.map((item, index) => `
            <div class="barcode-container">
              ${item.sku ? `<div class="barcode-header">${item.sku.substring(0, 2)}</div>` : ''}
              <svg id="barcode-${index}"></svg>
              ${item.productName ? `<div class="barcode-product-name">${item.productName} ${item.sizeInfo ? item.sizeInfo : ''}</div>` : ''}
              ${item.quantity ? `<div class="barcode-qty">NET QTY: ${item.quantity}</div>` : ''}
              ${item.extraInfo ? `<div class="barcode-info">${item.extraInfo}</div>` : ''}
            </div>
          `).join('')}
        </div>
        <script>
          window.onload = function() {
            ${barcodeData.map((item, index) => `
              JsBarcode("#barcode-${index}", "${item.code || item.sku}", {
                format: "CODE128",
                width: 2,
                height: 35,
                displayValue: true,
                fontSize: 14,
                margin: 5,
                background: "#ffffff"
              });
            `).join('')}
          }
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
};

// React component to display a barcode container
const BarcodeContainer: React.FC<{ barcodeData: BarcodeData[] }> = ({ barcodeData }) => {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      {barcodeData.map((item, index) => (
        <BarcodeComponent key={index} data={item} />
      ))}
    </div>
  );
};

// React component to display a single barcode
export const BarcodeComponent: React.FC<{
  data: BarcodeData;
  options?: BarcodeOptions;
}> = ({ data, options }) => {
  const defaultOptions: BarcodeOptions = {
    width: 2,
    height: 60,
    format: "CODE128",
    displayValue: true,
    fontSize: 14,
    margin: 5,
    background: '#ffffff',
  };

  const mergedOptions = { ...defaultOptions, ...options };
  const value = data.code || data.sku;

  return (
    <div className="barcode-display">
      {data.sku && <div className="barcode-title">{"GG"}</div>}
      <Barcode 
        value={value} 
        width={mergedOptions.width}
        height={mergedOptions.height}
        format={mergedOptions.format}
        displayValue={mergedOptions.displayValue}
        fontSize={mergedOptions.fontSize}
        margin={mergedOptions.margin}
        background={mergedOptions.background}
      />
      {data.sizeInfo && <div className="barcode-subtitle">{data.sizeInfo}</div>}
      {data.quantity && <div className="barcode-quantity">NET QTY: {data.quantity}</div>}
      {data.productName && <div className="barcode-product-name">{data.productName}</div>}
    </div>
  );
}; 